import { useCallback, useEffect, useState } from 'react'

import { useNativeBridge } from '@/context/NativeBridgeContext'
import { NotificationPermissionStatus } from '@/types'

interface UseNotificationPermissionResult {
    bridgeAvailable: boolean
    // Null until hydrated, or permanently when there's no bridge.
    status: NotificationPermissionStatus | null
    loading: boolean
    request: () => Promise<NotificationPermissionStatus | null>
}

export const useNotificationPermission = (): UseNotificationPermissionResult => {
    const bridge = useNativeBridge()
    const [status, setStatus] = useState<NotificationPermissionStatus | null>(null)
    const [loading, setLoading] = useState(bridge.isAvailable)

    useEffect(() => {
        if (! bridge.isAvailable) {
            return undefined
        }

        let cancelled = false

        bridge.getNotificationPermissionStatus()
            .then((result) => {
                if (! cancelled) {
                    setStatus(result)
                }
            })
            .catch(error => console.error(error))
            .finally(() => {
                if (! cancelled) {
                    setLoading(false)
                }
            })

        return () => {
            cancelled = true
        }
    }, [bridge])

    const request = useCallback(async (): Promise<NotificationPermissionStatus | null> => {
        if (! bridge.isAvailable) {
            return null
        }

        const result = await bridge.requestNotificationPermission()

        setStatus(result)

        return result
    }, [bridge])

    return {
        bridgeAvailable: bridge.isAvailable, status, loading, request,
    }
}
